import React from 'react'
import { Percentage } from 'src/components/text/percentage'
import { Strong } from 'src/components/text/strong'
import { ValueSwitch } from 'src/components/text/value-switch'
import { usePageContext } from 'src/contexts/page.context'
import { SecurityStyle, StockReportData } from 'src/shared/security.dto'
import { possessive } from 'src/shared/utils'

import { CurrentPriceFAQItem } from './current-price'
import { PriceForecast } from './price-forecast'

interface FAQItem {
  question: string
  answer: React.ReactElement
}

export const generateFaqList = ({
  security: { name, id, generatedAt, ratios, benchmarkIndex },
  profile: { directors },
  faqs: {
    currentYear,
    tradingAt,
    marketCap,
    priceMove365Days,
    buyOrSell,
    dividendYield,
    trailingDividend,
    totalDividend,
    exDividendDate,
    nextDividendPayDate,
    countryOfListing,
    exchange,
    tickerSymbol,
    style,
    analystConsensusDiscount,
    analystConsensusTargetPrice,
    epsForecast,
    percentageAgainst200DayMovingAverage,
    peRatio,
    majorShareHolders,
  },
}: StockReportData): FAQItem[] => {
  const { siteUrl } = usePageContext()
  return [
    {
      question: `What is the ${name} share price?`,
      answer: (
        <CurrentPriceFAQItem
          marketCap={marketCap}
          lastClose={tradingAt}
          generatedAt={generatedAt}
          id={id}
          name={name}
        />
      ),
    },
    {
      question: `How has the ${name} share price performed this year?`,
      answer: (
        <p>
          Shares in {name} last closed at <Strong>{tradingAt}</Strong> and the
          price had moved by{' '}
          <Strong>
            <Percentage value={priceMove365Days}>{priceMove365Days}</Percentage>
          </Strong>{' '}
          over the past 365 days. In terms of{' '}
          <a
            href={`${siteUrl}/ratios/relative-strength-1-year-767/`}
            target="_blank"
          >
            relative price strength
          </a>{' '}
          the {name} share price has{' '}
          <ValueSwitch
            value={ratios.momentum.relativeStrength['1yr'].value}
            positive={'outperformed'}
            negative={'underperformed'}
            neutral={'matched'}
          />{' '}
          the {benchmarkIndex} by{' '}
          <Strong>
            <Percentage value={ratios.momentum.relativeStrength['1yr'].value!}>
              {ratios.momentum.relativeStrength['1yr'].value}
            </Percentage>
          </Strong>{' '}
          over the past year.
        </p>
      ),
    },
    {
      question: `What are the analyst and broker recommendations for ${name}?`,
      answer: (() => {
        if (!buyOrSell) {
          return <p>There is no consensus recommendation for this security.</p>
        }
        return (
          <p>
            The overall consensus recommendation for {name} is{' '}
            <Strong>{buyOrSell}</Strong>. You can view the full broker
            recommendation list by unlocking its StockReport.
          </p>
        )
      })(),
    },
    {
      question: `What is the ${name} dividend yield?`,
      answer: dividendYield ? (
        <p>
          The {name} dividend yield is <Strong>{dividendYield}</Strong> based on
          the trailing twelve month period.
        </p>
      ) : (
        <p>{name} does not currently pay a dividend.</p>
      ),
    },
    {
      question: `Does ${name} pay a dividend?`,
      answer:
        totalDividend === 0 || !dividendYield ? (
          <p>{name} does not currently pay a dividend.</p>
        ) : (
          <p>
            Last year, {name} paid a total dividend of{' '}
            <Strong>{totalDividend}</Strong>, and it currently has a trailing
            dividend yield of <Strong>{trailingDividend}</Strong>.
            {exDividendDate && nextDividendPayDate && (
              <>
                Looking ahead, shares in {name} are due to go ex-dividend on{' '}
                <Strong>{exDividendDate}</Strong> and the next dividend pay date
                is <Strong>{nextDividendPayDate}</Strong>.
              </>
            )}
            {!exDividendDate && nextDividendPayDate && (
              <>
                Looking ahead, the next dividend pay date is{' '}
                <Strong>{nextDividendPayDate}</Strong>.
              </>
            )}
            {!nextDividendPayDate && (
              <>
                &nbsp;We do not have any data on when {name} is to next pay
                dividends.
              </>
            )}
          </p>
        ),
    },
    {
      question: `When does ${name} next pay dividends?`,
      answer:
        totalDividend === 0 || !dividendYield ? (
          <p>{name} does not currently pay a dividend.</p>
        ) : exDividendDate && nextDividendPayDate ? (
          <p>
            {name} are due to go ex-dividend on{' '}
            <Strong>{exDividendDate}</Strong> and the next dividend pay date is{' '}
            <Strong>{nextDividendPayDate}</Strong>. The historic dividend yield
            on {name} shares is currently <Strong>{trailingDividend}</Strong>.
          </p>
        ) : !exDividendDate || !nextDividendPayDate ? (
          <p>
            We do not have data on when {name} is to next pay dividends.&nbsp;
            The historic dividend yield on {name} shares is currently{' '}
            <Strong>{trailingDividend}</Strong>.
          </p>
        ) : !exDividendDate && nextDividendPayDate ? (
          <p>
            {name} are expected to next pay dividends on{' '}
            <Strong>{nextDividendPayDate}</Strong>.&nbsp; The historic dividend
            yield on {name} shares is currently{' '}
            <Strong>{trailingDividend}</Strong>.
          </p>
        ) : (
          <p>We do not have data on when {name} is to next pay dividends</p>
        ),
    },
    {
      question: `How do I buy ${name} shares?`,
      answer: (
        <p>
          To buy shares in {name} you'll need a{' '}
          <a
            href="https://help.stockopedia.com/product-guide/folios/faqs/which-are-the-cheapest-best-uk-brokers-for-stocks"
            rel="noopener"
          >
            share-dealing account
          </a>{' '}
          with an online or offline stock broker. Once you have opened your
          account and transferred funds into it, you'll be able to search and
          select shares to buy and sell. You can use Stockopedia’s{' '}
          <a href={siteUrl} target="_blank">
            share research software
          </a>{' '}
          to help you find the the kinds of shares that suit your investment
          strategy and objectives.
        </p>
      ),
    },
    {
      question: `What is the market cap of ${name}?`,
      answer: (
        <p>
          As of the previous close price of {tradingAt}, shares in {name} had a
          market capitalisation of <Strong>{marketCap}</Strong>.
        </p>
      ),
    },
    {
      question: `Where are ${name} shares listed?`,
      answer: (
        <>
          <p>Here are the trading details for {name}:&nbsp;</p>
          <ul>
            <li>
              Country of listing: <Strong>{countryOfListing}</Strong>
            </li>
            <li>
              Exchange: <Strong>{exchange}</Strong>
            </li>
            <li>
              Ticker Symbol: <Strong>{tickerSymbol}</Strong>
            </li>
          </ul>
        </>
      ),
    },
    {
      question: `What kind of share is ${name}?`,
      answer:
        style !== SecurityStyle.None ? (
          <p>
            Based on an overall assessment of its <Strong>quality</Strong>,{' '}
            <Strong>value</Strong> and <Strong>momentum</Strong> {name} is
            currently classified as a{' '}
            <Strong>
              <a
                href={`${siteUrl}/learn/stockranks-&-ratings/a-style-classification-for-every-stock-463128/`}
                target="_blank"
                className="bold underline app-grey-copy"
              >
                {style}
              </a>
            </Strong>
            . The classification is based on a{' '}
            <a
              href={`${siteUrl}/learn/stockranks-&-ratings/a-style-classification-for-every-stock-463128/`}
              target="_blank"
            >
              composite score
            </a>{' '}
            that examines a wide range of fundamental and technical measures.
            Stock are classified on the the following spectrum:{' '}
            <Strong>
              Super Stocks, High Flyers, Contrarians, Turnarounds, Neutral,
              Value Traps, Momentum Traps, Falling Stars, and Sucker Stocks
            </Strong>
            . For more information,{' '}
            <a
              href={`${siteUrl}/learn/stockranks-&-ratings/a-style-classification-for-every-stock-463128/`}
            >
              learn about our StockRank Styles
            </a>
            .
          </p>
        ) : (
          <>
            <p>
              Shares are classified based on a{' '}
              <a
                href="https://help.stockopedia.com/product-guide/stockranks/advanced/the-stockrank-styles"
                target="_blank"
                rel="noopener"
              >
                composite score
              </a>{' '}
              that examines a wide range of fundamental and technical measures.
              Stock are classified on the the following spectrum:{' '}
              <Strong>
                Super Stocks, High Flyers, Contrarians, Turnarounds, Neutral,
                Value Traps, Momentum Traps, Falling Stars, and Sucker Stocks
              </Strong>
              . For more information,{' '}
              <a
                href="https://help.stockopedia.com/product-guide/stockranks/advanced/the-stockrank-styles"
                rel="noopener"
              >
                learn about our StockRank Styles
              </a>
              .
            </p>
            <br />
            <p>{name} does not currently have a style classification.</p>
          </>
        ),
    },
    {
      question: `Is there a ${name} share price forecast for ${currentYear}?`,
      answer: (
        <PriceForecast
          name={name}
          tradingAt={tradingAt}
          analystConsensusTargetPrice={analystConsensusTargetPrice!}
          epsForecast={epsForecast}
          analystConsensusDiscount={analystConsensusDiscount}
        />
      ),
    },
    {
      question: `What is the trend in the ${name} share price?`,
      answer: (
        <>
          <p>
            An important predictor of whether a stock price will go up is its
            track record of momentum.&nbsp; Price trends tend to persist, so
            it's worth looking at them when it comes to a share like {name}.
            Over the past six months, its share price has{' '}
            <ValueSwitch
              value={ratios.momentum!.relativeStrength['6m'].value}
              positive={'outperformed'}
              negative={'underperformed'}
              neutral={'matched'}
            />{' '}
            the&nbsp;
            {benchmarkIndex} by{' '}
            <Strong>
              <Percentage
                value={ratios.momentum!.relativeStrength['6m'].value!}
              />
            </Strong>
            .
          </p>
          <p>
            As of the last closing price of <Strong>{tradingAt}</Strong>, shares
            in {name} were trading{' '}
            <Strong>
              <Percentage value={percentageAgainst200DayMovingAverage} />
            </Strong>
            &nbsp;
            <ValueSwitch
              value={percentageAgainst200DayMovingAverage}
              positive={'higher than'}
              negative={'below'}
              neutral={'matched'}
            />{' '}
            their 200 day moving average. You can read more about the power of
            momentum in{' '}
            <a
              target="_blank"
              href="https://app.stockopedia.com/learn/stockreports/spotting-strong-or-weak-momentum-463048"
              rel="noopener"
            >
              assessing share price movements
            </a>{' '}
            on Stockopedia.
          </p>
        </>
      ),
    },
    {
      question: `What is the ${name} PE Ratio?`,
      answer: !peRatio ? (
        <p>We are not able to find PE ratio data for {name}.</p>
      ) : (
        <>
          <p>
            The {name}{' '}
            <a
              href={`${siteUrl}/ratios/price-to-earnings-ratio-723/`}
              target="_blank"
            >
              PE ratio
            </a>{' '}
            based on its reported earnings over the past 12 months is{' '}
            <Strong>{peRatio}</Strong>. The shares last closed at{' '}
            <Strong>{tradingAt}</Strong>.&nbsp;
          </p>
          <p>
            The PE ratio (or price-to-earnings ratio) is the one of the most
            popular valuation measures used by stock market investors. It is
            calculated by dividing a company's price per share by its earnings
            per share.
          </p>
          <p>
            The PE ratio can be seen as being expressed in years, in the sense
            that it shows the number of years of earnings which would be
            required to pay back the purchase price, ignoring inflation. So in
            general terms, the higher the PE, the more expensive the stock is.
          </p>
        </>
      ),
    },
    {
      question: `Who are the key directors of ${name}?`,
      answer:
        directors && directors.length ? (
          <>
            <p>{possessive(name)} management team is headed by:&nbsp;</p>
            <ul>
              {directors.map((dirct, index) => (
                <li key={index}>
                  <Strong>{dirct.name}</Strong> - {dirct.position}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p>We do not have data on {possessive(name)} directors</p>
        ),
    },
    {
      question: `Who are the major shareholders of ${name}?`,
      answer:
        majorShareHolders && majorShareHolders.length ? (
          <>
            <p>
              Here are the top five shareholders of {name} based on the size of
              their shareholding:
            </p>
            <ul>
              {majorShareHolders.map((shareholder, index) => (
                <li key={index}>
                  <p>
                    <strong>{shareholder.name}</strong>{' '}
                    <span className="stk-text-tiny">{shareholder.type}</span>
                  </p>
                  <p>
                    <i>Percentage Owned:&nbsp;</i>{' '}
                    <strong>{shareholder.percentageOwned}%</strong>{' '}
                    <span className="stk-text-small">
                      ({shareholder.numShares} shares)
                    </span>
                  </p>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p>We do not have data on {possessive(name)} shareholders</p>
        ),
    },
  ]
}
